/**
 * Exit Intent Modal (Task 13)
 * Desktop: mouseleave toward top of viewport
 * Mobile: scroll-up velocity detection
 * Saves email to DB tagged with exit_intent: true
 * Only shows once per session (sessionStorage flag)
 */
import React, { useState, useEffect, useRef } from "react";
import { X, Mail, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getUTMPayload } from "@/utils/utmUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_FLAG = "pos_exit_intent_shown";

export default function ExitIntentModal({ lpSlug }) {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const lastScrollY = useRef(window.scrollY);
  const scrollVelocityRef = useRef(0);

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_FLAG)) return;

    // Desktop: mouseleave toward top
    const handleMouseLeave = (e) => {
      if (isMobile) return;
      if (e.clientY <= 10 && !sessionStorage.getItem(SESSION_FLAG)) {
        setVisible(true);
        sessionStorage.setItem(SESSION_FLAG, "1");
      }
    };

    // Mobile: fast scroll up
    let lastY = window.scrollY;
    let lastTime = Date.now();
    const handleScroll = () => {
      if (!isMobile) return;
      const currentY = window.scrollY;
      const currentTime = Date.now();
      const deltaY = lastY - currentY; // positive = scrolling up
      const deltaTime = currentTime - lastTime;

      if (deltaTime > 0) {
        scrollVelocityRef.current = deltaY / deltaTime; // px/ms
      }

      // Trigger on fast upward scroll (>1.5 px/ms)
      if (scrollVelocityRef.current > 1.5 && !sessionStorage.getItem(SESSION_FLAG)) {
        setVisible(true);
        sessionStorage.setItem(SESSION_FLAG, "1");
      }

      lastY = currentY;
      lastTime = currentTime;
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => setVisible(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const utms = getUTMPayload();
      const sid = localStorage.getItem("pos_session") || "";
      await axios.post(`${API}/lp/capture-email`, {
        email: email.trim(),
        name: "",
        source: "exit_intent",
        audience: lpSlug || "",
        ...utms,
      }, { headers: { "X-Session-ID": sid } });
      setSubmitted(true);
    } catch (err) {
      // Proceed to submitted state even on error
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative ap-card max-w-md w-full p-8 animate-in zoom-in duration-200">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--ap-muted)] hover:text-[var(--ap-ink)] transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[var(--ap-violet-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[var(--ap-violet)]" />
              </div>
              <h2 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-2">Before you go</h2>
              <p className="text-[var(--ap-muted)] text-sm leading-relaxed">
                Drop your email and we'll send you a reminder to upload your resume and check your free ATS score.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ap-muted)] w-4 h-4 z-10" />
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-[var(--ap-line)] rounded-lg focus:outline-none focus:border-[var(--ap-violet)] text-sm text-[var(--ap-ink)]"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !email}
                className="ap-btn ap-btn-fire w-full disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Remind Me To Upload
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <button
              onClick={handleClose}
              className="w-full text-[var(--ap-muted)] text-xs mt-3 hover:text-[var(--ap-ink)] transition-colors"
            >
              No thanks, I'll figure it out later
            </button>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[var(--ap-green)]" />
            </div>
            <h2 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-2">We'll remind you!</h2>
            <p className="text-[var(--ap-muted)] text-sm leading-relaxed">
              We've noted your email. We'll send you a reminder to upload your resume and check your free ATS score.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 text-[var(--ap-violet)] text-sm font-medium hover:underline"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
